import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { startCheckout } from "../lib/api";
import { Seo } from "../components/Seo";

export default function Cart() {
  const { items, subtotal, updateQty, removeItem } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const payload = items.map((i) => ({ product_id: i.product_id, quantity: i.quantity }));
      const { checkout_url } = await startCheckout(payload, window.location.origin);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error("Unable to start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="cart-page" className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
      <Seo title="Your Cart" description="Review your Peptide Labs N.I. research order." noindex />
      <div className="label-mono">Cart</div>
      <h1 className="mt-4 text-4xl lg:text-5xl tracking-tight text-white font-medium">Your Cart</h1>

      {items.length === 0 ? (
        <div className="mt-16 border border-[#2A2A2A] rounded-3xl p-16 text-center bg-[#0e0e0e]">
          <div className="text-[#B7BCC7]">Your cart is empty.</div>
          <Link to="/products" className="btn-primary inline-flex items-center gap-2 mt-6" data-testid="cart-empty-cta">
            Browse Products <ArrowRight size={16} />
          </Link>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-4">
            {items.map((i) => (
              <div
                key={i.product_id}
                data-testid={`cart-item-${i.slug}`}
                className="flex items-center gap-4 p-4 border border-[#2A2A2A] rounded-2xl bg-[#0e0e0e]"
              >
                <div className="w-20 h-24 rounded-xl vial-shell relative overflow-hidden shrink-0">
                  <div className="absolute left-0 right-0 bottom-0 h-[50%] vial-liquid" />
                </div>
                <div className="flex-1">
                  <div className="text-white font-medium tracking-tight">{i.name}</div>
                  <div className="text-[#B7BCC7] text-sm">{i.strength}</div>
                  <div className="mt-2 text-[#6E7784] text-xs font-mono">£{i.price.toFixed(2)} / vial</div>
                </div>
                <div className="flex items-center gap-3 border border-[#2A2A2A] rounded-full px-2 h-10">
                  <button
                    onClick={() => updateQty(i.product_id, i.quantity - 1)}
                    className="w-7 h-7 rounded-full hover:bg-[#171717] grid place-items-center"
                    data-testid={`cart-qty-dec-${i.slug}`}
                  >
                    <Minus size={12} />
                  </button>
                  <span className="w-6 text-center text-white font-mono text-sm">{i.quantity}</span>
                  <button
                    onClick={() => updateQty(i.product_id, i.quantity + 1)}
                    className="w-7 h-7 rounded-full hover:bg-[#171717] grid place-items-center"
                    data-testid={`cart-qty-inc-${i.slug}`}
                  >
                    <Plus size={12} />
                  </button>
                </div>
                <div className="w-24 text-right text-white font-mono">£{(i.price * i.quantity).toFixed(2)}</div>
                <button
                  onClick={() => removeItem(i.product_id)}
                  className="text-[#6E7784] hover:text-white p-2"
                  data-testid={`cart-remove-${i.slug}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          <aside className="lg:col-span-4 border border-[#2A2A2A] rounded-3xl bg-[#0e0e0e] p-8 h-fit">
            <div className="label-mono">Order Summary</div>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between text-[#B7BCC7]">
                <span>Subtotal</span>
                <span className="font-mono text-white" data-testid="cart-subtotal">£{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-[#B7BCC7]">
                <span>Shipping</span>
                <span className="font-mono">Calculated at checkout</span>
              </div>
            </div>
            <div className="mt-6 h-px bg-[#2A2A2A]" />
            <div className="mt-6 flex justify-between items-baseline">
              <span className="label-mono">Total</span>
              <span className="font-mono text-2xl text-white" data-testid="cart-total">£{subtotal.toFixed(2)}</span>
            </div>
            <button
              onClick={checkout}
              disabled={loading}
              data-testid="cart-checkout-btn"
              className="btn-primary w-full mt-8 disabled:opacity-60"
            >
              {loading ? "Redirecting…" : "Proceed to Checkout"}
            </button>
            <p className="mt-4 text-[11px] text-[#6E7784] leading-relaxed">
              Test card <span className="font-mono">4242 4242 4242 4242</span>, any future expiry, any CVC.
              For research use only.
            </p>
          </aside>
        </div>
      )}
    </div>
  );
}
